import { HttpEventType } from '@angular/common/http';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { AttachmentReadModel } from '@assetaccountant/api/models/attachment-read-model';
import { CreateAttachmentCommand } from '@assetaccountant/api/models/attachments/create-attachment.command';
import { DeleteAttachmentCommand } from '@assetaccountant/api/models/attachments/delete-attachment.command';
import { DownloadAttachmentCommand } from '@assetaccountant/api/models/attachments/download-attachment.command';
import { ListAttachmentQuery } from '@assetaccountant/api/models/attachments/list-attachment.query';
import { Feature } from '@assetaccountant/api/models/feature';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { AttachmentApi } from '@assetaccountant/api/services/attachment.api';
import { RegisterObjectId } from '@assetaccountant/core/models/register-object-id';
import { ModalService } from '@assetaccountant/core/services/modal.service';
import { downloadBlob } from '@assetaccountant/core/utils/file-utils';
import { TypedNgbModal, setupModal } from '@assetaccountant/core/utils/typed-modal';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { EditAttachmentLinkModalComponent } from '../../edit-attachment-link-modal/edit-attachment-link-modal.component';

type AttachmentAdjusted =
  | (AttachmentReadModel & { isExternalLink: true; externalStorageLink: string })
  | (AttachmentReadModel & { isExternalLink: false; externalStorageLink?: null });

@Component({
  selector: 'app-view-attachments2',
  templateUrl: './view-attachments2.component.html',
  styleUrls: ['./view-attachments2.component.scss'],
  standalone: false,
})
export class ViewAttachmentsComponent implements OnInit {
  @Input() id: RegisterObjectId;

  attachments: AttachmentAdjusted[] | null;
  uploading = false;

  readonly Feature = Feature;
  readonly RegisterPermission = RegisterPermissionType;

  constructor(
    private attachmentApi: AttachmentApi,
    private modalService: ModalService,
    @Inject(NgbModal) public ngbModal: TypedNgbModal,
    private toastrService: ToastrService,
  ) {}

  ngOnInit(): void {
    this.attachments = null;

    const listQuery: ListAttachmentQuery = { ...this.id };

    this.attachmentApi.list(listQuery).subscribe(response => {
      // TODO Can be removed if the API is updated to always return result
      if (Array.isArray(response)) {
        response = { result: response };
      }
      this.attachments = response.result as AttachmentAdjusted[];
      this.sortList();
    });
  }

  downloadAttachment(attachment: AttachmentAdjusted) {
    const downloadCommand: DownloadAttachmentCommand = {
      ...this.id,
      attachmentId: attachment.attachmentId,
    };

    this.attachmentApi.download(downloadCommand).subscribe(blob => {
      downloadBlob(blob, attachment.fileName);
    });
  }

  openLink(externalStorageLink: string) {
    window.open(externalStorageLink);
  }

  editAttachment(attachment: AttachmentAdjusted) {
    setupModal(
      this.ngbModal.open(EditAttachmentLinkModalComponent),
      modal => {
        modal.attachment = attachment;
      },
      modal =>
        modal.addlink.subscribe(editedAttachment => {
          this.addToList([editedAttachment as AttachmentAdjusted]);
        }),
    );
  }

  createLinkAttachment() {
    setupModal(
      this.ngbModal.open(EditAttachmentLinkModalComponent),
      modal => {
        modal.registerObjectId = this.id;
      },
      modal =>
        modal.addlink.subscribe(createdAttachment => {
          this.addToList([createdAttachment as AttachmentAdjusted]);
        }),
    );
  }

  deleteAttachment(attachment: AttachmentAdjusted) {
    const confirmationNote = attachment.isExternalLink
      ? '<p><b>Note:</b> Deleting this attachment only deletes it from FinQuery Fixed Assets. If this attachment has been posted to another platform you will need to manually delete it from that platform.</p>'
      : '';
    this.modalService
      .showQuestionModal(
        '<h4>Are you sure you want to delete this attachment?</h4>' + confirmationNote,
        'Delete Confirmation',
      )
      .subscribe(async _ => {
        const deleteCommand: DeleteAttachmentCommand = {
          ...this.id,
          attachmentId: attachment.attachmentId,
        };
        this.attachmentApi.delete(deleteCommand).subscribe(() => this.removeFromList(attachment));
      });
  }

  onFileInputChanged(element: HTMLInputElement) {
    if (element.files && element.files.length) {
      const filesToUpload: File[] = [];
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < element.files.length; i++) {
        const file = element.files[i];
        if (file.size > 51200000) {
          this.toastrService.error(
            `Cannot upload file ${file.name}, file size is greater than 50Mb.`,
          );
          return;
        }
        filesToUpload.push(file);
      }
      this.createFileAttachment(filesToUpload);
    }
  }

  createFileAttachment(files: File[]) {
    this.uploading = true;

    const createCommand: CreateAttachmentCommand = {
      objectType: this.id.objectType,
      objectId: this.id.objectId,
      file: files,
    };

    this.attachmentApi.create(createCommand).subscribe({
      next: response => {
        if ('result' in response) {
          this.uploading = false;
          const createdAttachments = (response.result ?? []) as AttachmentAdjusted[];
          this.addToList(createdAttachments);
        } else if ('type' in response && response.type === HttpEventType.UploadProgress) {
          // TODO Show as progress bar?
          if (response.total != null) {
            console.log(
              `Upload progress: ${((100 * response.loaded) / response.total).toFixed(2)}%`,
            );
          } else {
            console.log(`Upload progress: ${response.loaded} bytes`);
          }
        }
      },
      error: error => {
        this.uploading = false;
        throw error;
      },
    });
  }

  // Used to include newly created or updated attachments in our list
  private addToList(attachments: AttachmentAdjusted[]) {
    if (this.attachments == null) {
      this.attachments = [];
    }
    for (const attachment of attachments) {
      if (attachment == null) {
        continue;
      }
      const indexOfExisting = this.attachments.findIndex(
        a => a.attachmentId === attachment.attachmentId,
      );
      // If the attachemnt already exists, replace it as it may have been updated
      if (indexOfExisting >= 0) {
        this.attachments.splice(indexOfExisting, 1);
      }
      this.attachments.push(attachment);
    }
    this.sortList();
  }

  private removeFromList(attachment: AttachmentAdjusted) {
    if (this.attachments == null) {
      return;
    }
    this.attachments.splice(this.attachments.indexOf(attachment), 1);
  }

  private sortList() {
    if (this.attachments == null) {
      return;
    }
    // sort by last modified or created, then by name alphabetical
    this.attachments.sort((a, b) =>
      (a.modified ?? a.created) > (b.modified ?? b.created)
        ? -1
        : (a.modified ?? a.created) < (b.modified ?? b.created)
          ? 1
          : a.fileName < b.fileName
            ? -1
            : 1,
    );
  }
}
